import { AppLayout, PageBackLink } from '../../../components/layout';
import { LeaveActionToast } from '../../leave-management/components/LeaveActionToast';
import '../inventory.css';
import {
  fetchVendorById,
  // deleteVendor,
  // updateVendorStatus,
  fetchApproverDropdownOptions,
} from '../../../services/productVendorService';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, FileText } from 'lucide-react';
import i18n from "../../../i18n";
//import { Trans } from "react-i18next";

const TOAST_KEY = 'product_vendor_toast';

function translateVendorStatus(status) {
  if (status === 'Active') return i18n.t('status.active');
  if (status === 'Inactive') return i18n.t('status.inactive');
  if (status === 'Blacklisted') return i18n.t('inventory.blacklisted');
  return status;
}

function translateRiskLevelLabel(riskLevel) {
  if (riskLevel === 'Low') return i18n.t('inventory.lowRisk');
  if (riskLevel === 'Medium') return i18n.t('inventory.mediumRisk');
  if (riskLevel === 'High') return i18n.t('inventory.highRisk');
  return riskLevel ? i18n.t('inventory.riskLevelWithValue', { riskLevel }) : riskLevel;
}

function translateRiskLevelLower(riskLevel) {
  if (!riskLevel || riskLevel === 'Low') return i18n.t('inventory.lowRiskLower');
  return i18n.t('inventory.riskLevelLower', { riskLevel });
}

function translateSupplierType(type) {
  if (type === 'Manufacturer') return i18n.t('crm.manufacturer');
  return type;
}

function translateYesNo(value) {
  if (value === 'Yes') return i18n.t('common.yes');
  if (value === 'No') return i18n.t('common.no');
  return value;
}

export default function VendorDetail() {
  const { vendorId } = useParams();
  const navigate = useNavigate();

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  // const [deleting, setDeleting] = useState(false);
  // const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem(TOAST_KEY);
    if (!raw) return;
    try {
      setToast(JSON.parse(raw));
    } catch {
      setToast(null);
    }
    sessionStorage.removeItem(TOAST_KEY);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchVendorById(vendorId);
        if (cancelled) return;
        if (!data) {
          setVendor(null);
          setError(i18n.t('inventory.vendorNotFound'));
        } else {
          setVendor(data);
        }
      } catch (err) {
        if (!cancelled) {
          setVendor(null);
          setError(err?.message || i18n.t('inventory.failedToLoadVendor'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [vendorId]);

  // approvedBy is stored as an employee id; resolve it to a name for display.
  const [approverName, setApproverName] = useState('');
  useEffect(() => {
    if (!vendor?.approvedBy || vendor?.approvedByName) {
      setApproverName('');
      return undefined;
    }
    let cancelled = false;
    fetchApproverDropdownOptions({ size: 100 })
      .then((options) => {
        if (cancelled) return;
        const match = (options || []).find((o) => o.value === String(vendor.approvedBy));
        setApproverName(match?.label || '');
      })
      .catch(() => {
        if (!cancelled) setApproverName('');
      });
    return () => {
      cancelled = true;
    };
  }, [vendor?.approvedBy, vendor?.approvedByName]);

  // const onEdit = () => navigate(`/IMS/inventory/vendors/${vendorId}/edit`);
  const onEdit = () =>
  navigate(`/IMS/inventory/vendors/${vendorId}/edit`, {
    state: {
      from: 'details',
    },
  });
  const onBackToList = () => navigate('/IMS/inventory/vendors');

  // const handleDelete = async () => {
  //   if (!window.confirm('Delete this vendor permanently? This cannot be undone.')) return;
  //   setDeleting(true);
  //   try {
  //     await deleteVendor(vendorId);
  //     sessionStorage.setItem(
  //       TOAST_KEY,
  //       JSON.stringify({ message: 'Vendor deleted successfully.', type: 'success' })
  //     );
  //     navigate('/IMS/inventory/vendors');
  //   } catch (err) {
  //     setToast({ message: err?.message || 'Failed to delete vendor.', type: 'error' });
  //   } finally {
  //     setDeleting(false);
  //   }
  // };

  // const handleStatusChange = async (nextStatus) => {
  //   setStatusUpdating(true);
  //   try {
  //     const updated = await updateVendorStatus(vendorId, nextStatus);
  //     setVendor((prev) => ({
  //       ...prev,
  //       ...(updated || {}),
  //       status: updated?.status || nextStatus,
  //     }));
  //     setToast({ message: `Vendor status set to ${nextStatus}.`, type: 'success' });
  //   } catch (err) {
  //     setToast({ message: err?.message || 'Failed to update status.', type: 'error' });
  //   } finally {
  //     setStatusUpdating(false);
  //   }
  // };

  if (loading) {
    return (
      <AppLayout>
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-grey)', fontSize: '14px' }}>{i18n.t('inventory.loadingVendor')}</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!vendor) {
    return (
      <AppLayout>
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary-navy)' }}>
              {error || i18n.t('inventory.noVendorSelected')}
            </h3>
            <button type="button" className="btn-secondary" style={{ marginTop: '16px' }} onClick={onBackToList}>{i18n.t('inventory.backToVendorCatalog')}</button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const isBlacklisted = vendor.status === 'Blacklisted';

  const val = (text, { translateStatus = false, translateRisk = false, translateType = false, translateYesNoValue = false } = {}) => {
    if (!text || (typeof text === 'string' && !text.trim())) return i18n.t('inventory.notProvided');
    if (translateStatus) return translateVendorStatus(text);
    if (translateRisk) return translateRiskLevelLabel(text);
    if (translateType) return translateSupplierType(text);
    if (translateYesNoValue) return translateYesNo(text);
    return text;
  };

  const approverLabel = vendor.approvedByName || approverName || vendor.approvedBy;

  return (
    <AppLayout overlay={<LeaveActionToast toast={toast} />}>
      <div className="inventory-module">
    <div style={{ padding: '0 8px' }}>
      
      <PageBackLink to="/IMS/inventory/vendors" />

      <div className="page-header-responsive" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: '0' }}>
              {vendor.tradeName}
            </h1>
            
            <span style={{ 
              fontSize: '11px', 
              fontWeight: '700', 
              padding: '3px 10px', 
              borderRadius: '12px', 
              backgroundColor: vendor.status === 'Active' ? '#d1fae5' : isBlacklisted ? '#fee2e2' : '#f1f5f9', 
              color: vendor.status === 'Active' ? '#065f46' : isBlacklisted ? '#991b1b' : '#475569',
              textTransform: 'capitalize'
            }}>
              {translateVendorStatus(vendor.status)}
            </span>

            <span style={{ 
              fontSize: '11px', 
              fontWeight: '700', 
              padding: '3px 10px', 
              borderRadius: '12px', 
              backgroundColor: vendor.riskLevel === 'Low' ? '#e0f2fe' : vendor.riskLevel === 'Medium' ? '#fef3c7' : '#ffedd5',
              color: vendor.riskLevel === 'Low' ? '#0369a1' : vendor.riskLevel === 'Medium' ? '#b45309' : '#c2410c',
              textTransform: 'capitalize'
            }}>
              {translateRiskLevelLower(vendor.riskLevel)}
            </span>
          </div>

          <div style={{ fontSize: '13px', color: 'var(--text-grey)', marginTop: '6px' }}>
            {vendor.legalCompanyName || vendor.tradeName} &bull; {vendor.type ? translateSupplierType(vendor.type) : i18n.t('inventory.emDash')} {vendor.addressCity || vendor.addressState ? `· ${[vendor.addressCity, vendor.addressState].filter(Boolean).join(', ')}` : ''}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* <select
            className="form-select"
            value={vendor.status}
            disabled={statusUpdating}
            onChange={(e) => handleStatusChange(e.target.value)}
            style={{ height: '36px', minWidth: '140px', fontSize: '13px' }}
            aria-label="Update vendor status"
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Blacklisted">Blacklisted</option>
          </select> */}
          <button 
            type="button" 
            onClick={onEdit}
            style={{ 
              border: '1px solid var(--border-color)', 
              backgroundColor: '#ffffff', 
              color: 'var(--text-dark)', 
              padding: '6px 16px', 
              borderRadius: '6px', 
              fontSize: '13px', 
              fontWeight: '600', 
              cursor: 'pointer',
              transition: 'var(--transition-all)',
              boxShadow: 'var(--shadow-sm)',
              height: '36px'
            }}
          >
            {i18n.t('common.edit')}
          </button>
          {/* <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            style={{
              border: '1px solid rgba(239,68,68,0.35)',
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              padding: '6px 14px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: deleting ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              opacity: deleting ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} />
            {deleting ? 'Deleting…' : 'Delete'}
          </button> */}
        </div>
      </div>

      {isBlacklisted && (
        <div style={{ display: 'flex', gap: '12px', padding: '16px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '12px', color: '#991b1b', marginBottom: '24px', alignItems: 'flex-start' }}>
          <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '700' }}>{i18n.t('inventory.vendorAccountStatusBlacklisted')}</h4>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.45', opacity: 0.95 }}>
              {i18n.t('inventory.vendorBlacklistedHint')}
            </p>
          </div>
        </div>
      )}

      <div className="detail-card-container" style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)', padding: '32px', marginBottom: '28px' }}>
        
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--accent-blue)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
            {i18n.t('inventory.basicInformationSection')}
          </h3>
          <div className="grid-4-cols">
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.tradeName')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.tradeName)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.legalName')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.legalCompanyName)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.vendorType')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.type, { translateType: true })}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.registrationNo')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', fontFamily: 'monospace' }}>{val(vendor.companyRegNumber)}</span>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', marginBottom: '28px' }} />

        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--primary-navy)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
            {i18n.t('inventory.contactsSection')}
          </h3>
          
          <div className="grid-4-cols" style={{ marginBottom: '20px' }}>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('crm.primaryContact')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>
                {vendor.contactPerson ? `${vendor.contactPerson}${vendor.phone ? ` · ${vendor.phone}` : ''}` : i18n.t('inventory.notProvided')}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.primaryEmail')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600' }}>{val(vendor.email)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.secondaryContact')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>
                {vendor.secondaryName ? `${vendor.secondaryName}${vendor.secondaryMobile ? ` · ${vendor.secondaryMobile}` : ''}` : i18n.t('inventory.notProvided')}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.officePhone')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.alternatePhone)}</span>
            </div>
          </div>

          <div className="grid-2-cols">
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('formActions.address')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>
                {[vendor.addressLine1, vendor.addressLine2, vendor.address].filter(Boolean).join(', ') || i18n.t('inventory.notProvided')}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.cityStatePin')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>
                {[vendor.addressCity, vendor.addressState, vendor.addressPincode].filter(Boolean).join(', ') || i18n.t('inventory.notProvided')}
              </span>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', marginBottom: '28px' }} />

        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--primary-navy)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
            {i18n.t('inventory.taxComplianceSection')}
          </h3>
          <div className="grid-4-cols">
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.gstin')}</span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-dark)', fontFamily: 'monospace' }}>{val(vendor.taxId)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.pan')}</span>
              <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-dark)', fontFamily: 'monospace' }}>{val(vendor.pan)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.msmeUdyam')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.msmeRegNo)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.cin')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', fontFamily: 'monospace' }}>{val(vendor.cin)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.tan')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', fontFamily: 'monospace' }}>{val(vendor.tan)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.iec')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', fontFamily: 'monospace' }}>{val(vendor.iec)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.isoCertifications')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.isoCertifications)}</span>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', marginBottom: '28px' }} />

        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--primary-navy)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
            {i18n.t('inventory.bankingDetailsSection')}
          </h3>
          <div className="grid-4-cols">
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.bankNameBranch')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.bankName)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.accountNo')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', fontFamily: 'monospace' }}>{val(vendor.accountNumber)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.ifscCode')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', fontFamily: 'monospace' }}>{val(vendor.ifscCode)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.swift')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.swiftCode)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.upiId')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.upiId)}</span>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', marginBottom: '28px' }} />

        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--primary-navy)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
            {i18n.t('inventory.businessDetailsSection')}
          </h3>
          <div className="grid-2-cols">
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.productsServicesOffered')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.description)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.authorizedDealerOem')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.isAuthorized, { translateYesNoValue: true })}</span>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', marginBottom: '28px' }} />

        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--primary-navy)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
            {i18n.t('inventory.commercialDetailsSection')}
          </h3>
          <div className="grid-4-cols">
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.standardPaymentTerms')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.paymentTerms)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.creditPeriod')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>
                {vendor.creditPeriod !== undefined && vendor.creditPeriod !== null
                  ? i18n.t('inventory.daysCount', { count: vendor.creditPeriod })
                  : i18n.t('inventory.notProvided')}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('productMetrics.currency')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.currency || 'INR')}</span>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', marginBottom: '28px' }} />

        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--primary-navy)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
            {i18n.t('inventory.documentsSection')}
          </h3>
          {vendor.uploadedFiles && vendor.uploadedFiles.length > 0 ? (
            <div className="inv-responsive-grid inv-responsive-grid--compact" style={{ gap: '12px' }}>
              {vendor.uploadedFiles.map((file, idx) => (
                <div key={file.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', border: '1px solid var(--border-color)', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                  <FileText size={18} style={{ color: 'var(--accent-blue)' }} />
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-dark)' }}>{file.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <span style={{ fontSize: '14px', color: 'var(--text-grey)', fontStyle: 'italic' }}>{i18n.t('inventory.noKycDocumentsAttached')}</span>
          )}
        </div>

        <div style={{ borderTop: '1px solid var(--border-color)', marginBottom: '28px' }} />

        <div>
          <h3 style={{ fontSize: '13px', fontWeight: '800', color: 'var(--primary-navy)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px' }}>
            {i18n.t('inventory.internalApprovalsSection')}
          </h3>
          <div className="grid-4-cols">
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.vendorStatus')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.status, { translateStatus: true })}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.riskLevelLabel')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.riskLevel, { translateRisk: true })}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.approvedBy')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(approverLabel)}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>{i18n.t('inventory.approvalDate')}</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{val(vendor.approvalDate)}</span>
            </div>
          </div>
        </div>

      </div>

      <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)', padding: '28px 32px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '700', color: 'var(--primary-navy)', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px', marginBottom: '20px' }}>
          {i18n.t('inventory.defectHistoryTitle')}
        </h3>
        <div style={{ padding: '24px', textAlign: 'center', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed var(--border-color)' }}>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-grey)' }}>{i18n.t('inventory.noDefectiveReceipts')}</p>
        </div>
      </div>

    </div>
      </div>
    </AppLayout>
  );
}
